import { motion } from "motion/react"
import { useState } from "react"
import { Navigate } from "react-router"

import { academyNameTrademarkNotice, getAcademyNameValidationError } from "@forento/core/utilities/academyName"
import Form from "@forento/ui/components/Form"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import InputField from "~/components/InputField"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import trpc, { getInputErrors } from "~/utilities/trpc"

import Layout, { Logo } from "./Layout"
import { authCardClass, staggerContainer, staggerItem } from "./Layout/authCard"

const CreatePlatformPage: React.FC = () => {
	const user = useUser()
	const alert = useAlert()

	const [name, setName] = useState("")
	const [isAccountEmailChecked, setAccountEmailChecked] = useState(true)
	const [contactEmail, setContactEmail] = useState("")
	const [isSubmitting, setSubmitting] = useState(false)

	const handleSubmit = async () => {
		const academyNameError = getAcademyNameValidationError(name)
		if (academyNameError !== null) {
			await alert.show("Invalid academy name", academyNameError)
			return
		}
		if (!isAccountEmailChecked && contactEmail.trim().length === 0) return
		setSubmitting(true)
		try {
			await trpc.platform.create.mutate({
				name,
				contactEmail: isAccountEmailChecked ? user.user!.email : contactEmail,
			})
			await user.reload()
		} catch (error) {
			setSubmitting(false)
			const errors = getInputErrors(error)
			if ("contactEmail" in errors) {
				await alert.show("Error", "The contact email address is invalid.")
			} else {
				console.error(error)
			}
		}
		setSubmitting(false)
	}

	if (user.user!.platform !== null) return <Navigate to={routes.account.selectFindSource()} />

	return (
		<Layout>
			<Logo />
			<Form onSubmit={handleSubmit} className={authCardClass}>
				<motion.div
					className="flex w-full flex-col items-stretch gap-0"
					variants={staggerContainer}
					initial="hidden"
					animate="visible"
				>
					<motion.div variants={staggerItem}>
						<h1 className="text-left text-xl font-bold tracking-tight text-zinc-900">
							Create your academy
						</h1>
						<p className="mt-1.5 text-left text-sm text-zinc-500">
							Give it a name to start with. You can change it anytime.
						</p>
					</motion.div>

					<motion.div variants={staggerItem} className="mt-6">
						<InputField
							label="Academy name"
							placeholder="e.g. Dave's Courses"
							value={name}
							onChange={setName}
						/>
						<p className="mt-2 text-left text-xs leading-relaxed text-zinc-500">
							{academyNameTrademarkNotice}
						</p>
					</motion.div>

					<motion.div variants={staggerItem} className="mt-4">
						<Checkbox
							label="Use my account's email address"
							isChecked={isAccountEmailChecked}
							onChange={setAccountEmailChecked}
							className="mb-2"
						/>
						{isAccountEmailChecked ? (
							<InputField label="Contact email address" value={user.user!.email} disabled />
						) : (
							<InputField
								label="Contact email address"
								placeholder="e.g. contact@davescourses.com"
								value={contactEmail}
								onChange={setContactEmail}
							/>
						)}
						<p className="mt-2 text-left text-xs leading-relaxed text-zinc-500">
							The contact email will be public to your users, and will also be used in your terms of
							service.
						</p>
					</motion.div>

					<motion.div variants={staggerItem} className="mt-6">
						<SubmitButton
							variant="primary"
							isLoading={isSubmitting}
							className="w-full cursor-pointer rounded-xl py-3 text-sm font-semibold"
						>
							Create academy
						</SubmitButton>
					</motion.div>

					<motion.div variants={staggerItem} className="mt-4 flex justify-center">
						<Button
							variant="link"
							onClick={user.signOut}
							className="cursor-pointer text-sm text-zinc-500 underline-offset-2 hover:text-zinc-900"
						>
							Sign out
						</Button>
					</motion.div>
				</motion.div>
			</Form>
		</Layout>
	)
}

export default CreatePlatformPage
